import React, { Key } from 'react';
import { Item } from '@react-stately/collections';
import { AsyncListData } from '@react-stately/data';
import { MenuTriggerAction } from '@react-types/combobox';

import {
  DropDownIcon,
  InternationalizationNotSupportedError,
  ListElement,
  OverlayOptions,
  TickIcon
} from '@nw/omega-design-system';

import { ComboBoxComponent } from './Combobox';

export interface ComboBoxWrapperTypes<T> {
  label: string;
  onSelectionChange: (e: Key) => void;
  selectedKey: string;
  placeholder: string;
  getListItemKey: (T: any) => string;
  renderListItem?: any;
  onBlur?: (e: React.FocusEvent) => void;
  onInputChange: (value: string) => void;
  onOpenChange: (
    isOpen: boolean,
    menuTrigger?: MenuTriggerAction | undefined
  ) => void;
  error: string | undefined;
  fieldContainerStyles?: string;
  wrapperWithPopOverStyles?: string;
  inputGroupStyles?: string;
  inputFieldStyles?: string;
  buttonStyles?: string;
  errorMessageStyles?: string;
  popOverStyles?: string;
  listBoxStyles?: string;
  dropDownIcon?: any;
  selectedOptionIcon?: any;
  defaultFilter?: any;
  isDisabled?: boolean;
  apiStatus?: string;
  autoFocus?: boolean;
  listItemStyles?: string;
  itemContentStyles?: string;
  overlayOptions?: OverlayOptions;
  getListItemTextValue?: (T: any) => any;
  getInputTextFromValue;
  getValueFromInputText;
  initialValue: string;
  list: AsyncListData<any>;
  getDisplayValueFromSelectedState?: (T: any) => string;
  mobilePlaceholder?: string;
  helperText?: string;
  hintClassName?: string;
  enableDataMask?: boolean;
}

export function ComboBox<T>(props: ComboBoxWrapperTypes<T>) {
  //NOTE: react-aria supports Internalization (uses Intl.PluralRules) which is only supported in iOS >=13
  if (typeof Intl.PluralRules !== 'function') {
    throw new InternationalizationNotSupportedError(
      'Internalization is not supported'
    );
  }

  const {
    label,
    onSelectionChange,
    selectedKey,
    placeholder,
    getListItemKey,
    renderListItem: RenderListView,
    onBlur,
    onInputChange,
    onOpenChange,
    error,
    isDisabled,
    apiStatus,
    dropDownIcon = DropDownIcon,
    selectedOptionIcon = TickIcon,
    autoFocus,
    fieldContainerStyles,
    wrapperWithPopOverStyles,
    inputFieldStyles,
    buttonStyles,
    popOverStyles,
    listBoxStyles,
    listItemStyles,
    itemContentStyles,
    overlayOptions,
    getListItemTextValue,
    getInputTextFromValue,
    getValueFromInputText,
    initialValue,
    list,
    getDisplayValueFromSelectedState,
    helperText,
    hintClassName,
    enableDataMask,
    ...others
  } = props;

  return (
    <ComboBoxComponent
      label={label}
      onSelectionChange={onSelectionChange}
      selectedKey={selectedKey}
      placeholder={placeholder}
      onBlur={onBlur}
      onInputChange={onInputChange}
      onOpenChange={onOpenChange}
      error={error}
      isDisabled={isDisabled}
      apiStatus={apiStatus}
      dropDownIcon={dropDownIcon}
      autoFocus={autoFocus}
      fieldContainerStyles={fieldContainerStyles}
      wrapperWithPopOverStyles={wrapperWithPopOverStyles}
      inputFieldStyles={inputFieldStyles}
      buttonStyles={buttonStyles}
      popOverStyles={popOverStyles}
      listBoxStyles={listBoxStyles}
      listItemStyles={listItemStyles}
      itemContentStyles={itemContentStyles}
      selectedOptionIcon={selectedOptionIcon}
      overlayOptions={overlayOptions}
      getInputTextFromValue={getInputTextFromValue}
      getValueFromInputText={getValueFromInputText}
      initialValue={initialValue}
      list={list}
      getDisplayValueFromSelectedState={getDisplayValueFromSelectedState}
      helperText={helperText}
      hintClassName={hintClassName}
      enableDataMask={enableDataMask}
      {...others}
    >
      {(eachItem) => (
        <Item
          key={getListItemKey(eachItem)}
          textValue={
            getListItemTextValue
              ? getListItemTextValue(eachItem)
              : getListItemKey(eachItem)
          }
        >
          {RenderListView ? (
            <RenderListView item={eachItem} />
          ) : (
            <ListElement item={eachItem} />
          )}
        </Item>
      )}
    </ComboBoxComponent>
  );
}
